import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const UNDO_WINDOW_MS = 10_000;

/**
 * Hook for admin delete with:
 *  1. In-app confirmation modal (ConfirmDeleteModal)
 *  2. 10-second "Undo" toast safety net — delete fires only AFTER the grace window.
 *
 * Returns: { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin }
 *
 * Delete buttons MUST be gated on `isAdmin` in every page so non-admins don't see them.
 */
export function useAdminDelete(onSuccess) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [deleteState, setDeleteState] = useState({ open: false, url: '', title: '', message: '' });
  // Tracks pending (undo-able) deletes keyed by toast id so we can cancel the timer on Undo.
  const pendingRef = useRef(new Map());

  const requestDelete = useCallback((url, itemName, details) => {
    if (!isAdmin) { toast.error('Admin only'); return; }
    setDeleteState({
      open: true,
      url,
      itemName: itemName || 'item',
      title: `Delete "${itemName}"?`,
      message: details || 'This will permanently delete this entry and reverse all linked data (stock, revenue, lots). You\'ll have 10 seconds to undo.',
    });
  }, [isAdmin]);

  const cancelDelete = useCallback(() => {
    setDeleteState({ open: false, url: '', title: '', message: '' });
  }, []);

  const confirmDelete = useCallback(async () => {
    const { url, itemName } = deleteState;
    setDeleteState(prev => ({ ...prev, open: false }));
    if (!url) return;

    // Schedule the actual DELETE 10s from now; user can Undo during the window.
    const toastId = toast(`Deleting "${itemName}"…`, {
      description: 'Click Undo within 10 seconds to cancel.',
      duration: UNDO_WINDOW_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          const entry = pendingRef.current.get(toastId);
          if (entry) {
            clearTimeout(entry.timer);
            pendingRef.current.delete(toastId);
            toast.success('Delete cancelled');
          }
        },
      },
    });

    const timer = setTimeout(async () => {
      pendingRef.current.delete(toastId);
      try {
        const headers = {};
        // Recipes require a password token header (else backend returns 401)
        if (url.includes('/recipes/')) {
          const tok = sessionStorage.getItem('ti_recipe_token');
          if (tok) headers['X-Recipe-Token'] = tok;
        }
        await axios.delete(`${API}${url}`, { withCredentials: true, headers });
        toast.success(`"${itemName}" deleted`);
        if (onSuccess) onSuccess();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Delete failed');
      }
    }, UNDO_WINDOW_MS);

    pendingRef.current.set(toastId, { timer, url });
  }, [deleteState, onSuccess]);

  return { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin };
}
